<template>
  <AppSection id="scopes" :title="$getConst.pages.pageUsers.users" type="page">
    <AppSection
      id="list-users"
      type="section"
      :button="hasPermission($getConst.permissions.addUser)"
      :title="$getConst.pages.pageUsers.list_of_users"
      :button-text="$getConst.pages.button.add"
      :button-icon="$getConst.icons.add"
      @onClick="openUserAddModal"
      :second-button="hasPermission($getConst.permissions.exportUsers)"
    >
      <template #secondButton v-if="hasPermission($getConst.permissions.exportUsers)">
        <b-button @click="exportExcel" variant="link" class="ml-auto text-success font-weight-bold">
          <AppSvgIcon class="mr-2" :icon="$getConst.svgIcons.excel"/>{{$t($getConst.pages.excel.excel) | capitalize}}
        </b-button>
      </template>

      <AppDataTable
        ref="usersTable"
        :fields="fields"
        :actions="actions"
        end-point="users"
        @onEdit="edit"
        @onLogAs="logAs"
        @onAddFilter="filtering"
      >

        <template v-slot:name="{data,row}">
          <b-link @click="goToUserDetails(row.id)">{{row.name}}<i class="fa fa-external-link ml-2"></i></b-link>
        </template>

        <template v-slot:isSignUpCompleted="item">
          <b-badge v-if="item.data.toLowerCase() === 'completed'" class="gradient" pill variant="secondary">
            {{$t($getConst.pages.pageUsers.signup_completed)}}
          </b-badge>
          <b-badge v-else-if="item.data.toLowerCase() === 'waiting'" class="gradient" pill variant="warning">
            {{$t($getConst.pages.pageUsers.invitation_sent)}}
          </b-badge>
          <b-badge v-else-if="item.data.toLowerCase() === 'expired'" class="gradient" pill variant="danger">
            {{$t($getConst.pages.pageUsers.invitation_expired)}}
          </b-badge>
        </template>

        <template #button-onLogAs="{action, rowData}">
          <b-button
            v-if="hasAnyOfPermissions(action.permissions)"
            :variant="action.variant"
            class="ml-2 border-0"
            size="sm"
            :disabled="!isInSameScope(rowData) || isSuperAdmin(rowData.role.name)"
            @click="logAs(rowData)">
            <i :class="action.icon"></i>
          </b-button>
        </template>

        <template #button-onResendInvitation="{action, rowData}">
          <b-button
            v-if="hasAnyOfPermissions(action.permissions)"
            :variant="action.variant"
            class="ml-2 border-0"
            size="sm"
            :disabled="rowData.isSignUpCompleted.toLowerCase() === 'completed' || isResendingInvitation || usersListHavingInvitationResent.includes(rowData.id)"
            @click="resendInvitation(rowData.id)">
            <i :class="action.icon"></i>
          </b-button>
        </template>

        <template #button-onResetPassword="{action, rowData}">
          <b-button
            v-if="isAdmin($store.getters['auth/role'])"
            :variant="action.variant"
            class="ml-2 border-0"
            size="sm"
            @click="resetPassword(rowData.id)">
            <i :class="action.icon"></i>
          </b-button>
        </template>
        <template #button-onDeleteUser="{action, rowData}">
          <b-button
            :variant="action.variant"
            class="ml-2 border-0"
            size="sm"
            :disabled="isSuperAdmin(rowData.role.name)  || (isLocalAdmin($store.getters['auth/role']) && (isLocalAdmin(rowData.role.name)))"
            @click="confirmDeleteUser(rowData)">
            <i :class="action.icon"></i>
          </b-button>
        </template>

        <template v-slot:is_enabled="item">
          <template v-if="hasPermission($getConst.permissions.enableDisableUser) && $store.getters['auth/id'] != item.row.id && item.row.role.name != 'Super Admin'">
            <b-spinner v-if="enablingUserId == item.row.id" small type="grow" label="Spinning"></b-spinner>
            <template v-else>
              <b-button class="border-0" size="sm" variant="outline-success" v-if="item.data"
                        @click="enableOrDisableUser(item.row.id)">
                <i :class="$getConst.icons.power_off"></i>
              </b-button>
              <b-button class="border-0" size="sm" variant="outline-danger" v-else
                        @click="enableOrDisableUser(item.row.id)">
                <i :class="$getConst.icons.power_off"></i>
              </b-button>
            </template>
          </template>
          <template v-else>
            <span class="text-success" v-if="item.data">{{$t($getConst.pages.pageUsers.enabled) | capitalize}}</span>
            <span class="text-danger" v-else>{{$t($getConst.pages.pageUsers.disabled) | capitalize}}</span>
          </template>
        </template>

      </AppDataTable>
    <UserModal :id="userAddModal"/>
    <UserModal :id="userAssignModal" :user-to-edit="userToEdit"/>

      <AppConfirmationModal
        id="confirmation-modal"
        :title=$t($getConst.pages.profile.are_you_sure) | capitalize
        ref="confirmDelete"
        @onCancel="cancelDeleteUser"
        @onConfirm="deleteUser"/>

    </AppSection>
  </AppSection>
</template>
<script>
  import AppSection from "../../components/common/sections/AppSection";
  import AppDataTable from "../../components/common/table/AppDataTable";
  import UserModal from "./UserModal";
  import {
    exportUsers,
    logAs,
    resendSignUpInvitationEmail,
    deleteUser,
    enableOrDisableUser,
    resendResetingPasswordEmail
  } from "../../api/user.api";
  import {snotifyConfig} from "../../shared/constants/snotify";
  import {getAllRoles} from "../../api/role.api";
  import AppConfirmationModal from "../../components/common/modals/AppConfirmationModal";


  export default {
    name: "index",
    components: {AppSection, AppDataTable, UserModal, AppConfirmationModal},
    data() {
      return {
        endPoint: null,
        users: [],
        roles: [],
        userToEdit: null,
        userAddModal: 'user-add-modal',
        userAssignModal: 'user-assign-modal',
        isResendingInvitation: false,
        isResetingPassword: false,
        enablingUserId: '',
        usersListHavingInvitationResent: [],
        filters:[],
        fields: [
          {key: 'id', label:'ID', sortable: true, filterable: true},
          {key: 'name', label:'Name', sortable: true, filterable: true, sortKey: 'firstName'},
          {key: 'email', label:'Email', sortable: true, filterable: true},
          {key: 'roles.name', label: 'roles', sortable: false, filterable: true, multiple: true},
          {key: 'scopes.name', label: 'Scopes', sortable: false, filterable: true},
          {key: 'isSignUpCompleted', label: 'status', filterable: true},
          {key: 'is_enabled', label: 'Enabled/Disabled', sortable: true, filterable: true},
        ],
        actions: [
          {action: 'onEdit', variant: 'outline-secondary', icon: this.$getConst.icons.edit, permissions: [this.$getConst.permissions.updateUser]},
          {action: 'onLogAs', variant: 'outline-warning', icon: this.$getConst.icons.eye, permissions: [this.$getConst.permissions.impersonateUser]},
          {action: 'onResendInvitation', variant: 'outline-success', icon: this.$getConst.icons.paper_plan, permissions: [this.$getConst.permissions.impersonateUser]},
          {action: 'onResetPassword', variant: 'outline-secondary', icon: this.$getConst.icons.refresh, permissions: [this.$getConst.permissions.resetPassword]},
          {action: 'onDeleteUser', variant: 'ouIS_ABSENCES_PER_DAYStline-danger', icon: this.$getConst.icons.delete, permissions: [this.$getConst.permissions.deleteUser]},
        ],
        userToDelete: null
      }
    },
    created() {
      this.getRoles();

      this.updateScopeField(this.$store.getters['auth/allScopes']);
      this.updateStatusField();

       this.fields[6].filterable = [
          { value: '1', text: this.$t(this.$getConst.pages.pageUsers.enabled) },
          { value: '0', text: this.$t(this.$getConst.pages.pageUsers.disabled) }
       ];
    },
    mounted() {
      if(this.$refs.usersTable) {
        this.$root.$on('onEditUser', user => {
          this.onAssignUserToleScope(user);
        });
        this.$root.$on('onAddUser', user => {
          this.onAddUser(user);
        });
      }
    },
    methods: {
      filtering(filters){
        this.filters = filters
      },
      edit(user) {
        this.userToEdit = user;
        this.openAssignUserModal();
      },
      resendInvitation(userId) {
        this.isResendingInvitation = true;
        this.$snotify.async(
          this.$t(this.$getConst.pages.pageUsers.resending_invitation),
          this.$t(this.$getConst.pages.alerts.please_wait),
          () => new Promise((resolve, reject) => {
            resendSignUpInvitationEmail(userId).then(
              (response) => {
                this.$refs.usersTable.editItem(response.data);
                this.usersListHavingInvitationResent.push(userId);
                this.isResendingInvitation = false;
                resolve({
                  title: this.$t(this.$getConst.pages.alerts.success),
                  body: this.$t(this.$getConst.pages.alerts.success_operation),
                  config: snotifyConfig,
                })
              },
              (error) => {
                const errorBody = this.resolveErrorMessage(error);
                this.isResendingInvitation = false;
                reject({
                  title: errorBody.code,
                  body: errorBody.message,
                  config: snotifyConfig
                });
              }
            )
          })
        )
      },
      resetPassword(userId) {
        this.isResetingPassword = true;
        this.$snotify.async(
          this.$t(this.$getConst.pages.pageUsers.reseting_password),
          this.$t(this.$getConst.pages.alerts.please_wait),
          () => new Promise((resolve, reject) => {
            resendResetingPasswordEmail(userId).then(
              (response) => {
                this.$refs.usersTable.editItem(response.data);
                this.isResetingPassword = false;
                resolve({
                  title: this.$t(this.$getConst.pages.alerts.success),
                  body: this.$t(this.$getConst.pages.alerts.success_operation),
                  config: snotifyConfig,
                })
              },
              (error) => {
                const errorBody = this.resolveErrorMessage(error);
                this.isResetingPassword = false;
                reject({
                  title: errorBody.code,
                  body: errorBody.message,
                  config: snotifyConfig
                });
              }
            )
          })
        )
      },
      openUserAddModal() {
        this.$bvModal.show(this.userAddModal);
      },
      openAssignUserModal() {
        this.$bvModal.show(this.userAssignModal);
      },
      onAssignUserToleScope(user) {
        this.$snotify.success(this.$t(this.$getConst.pages.pageUsers.user_updated));
        this.$refs.usersTable.editItem(user);
      },
      onAddUser(user) {
        this.$snotify.success(this.$t(this.$getConst.pages.pageUsers.user_added));
        this.$refs.usersTable.addItem(user);
      },
      logAs(user) {
         if (this.usersHasSameScopeAsOrigin(user)) {
          logAs(user.id).then(
            response => {
              sessionStorage.setItem("loggedAs", JSON.stringify(response.data));
              this.$store.dispatch("auth/logAs", response.data);
              if(user.role.name === 'Vendor') {
                this.$router.push("/invoices");
              } else if (user.role.name === 'Worker' || user.role.name === 'Approver' || user.role.name === "Freelancer") {
                this.$router.push("/timesheets");
              }
              this.$store.dispatch("loader/hideLoadApp");
            },
            error => {
              this.$store.dispatch("loader/hideLoadApp");
              this.handleApiError(error);
            }
          )
        } else {
           let redirectUri = user.scopes[0].url + "/timesheets#loggedAs=" + user.id;

           let toastClosed = false;

           this.$snotify.confirm(this.$t(this.$getConst.pages.alerts.redirecting_to_other_scope) + user.scopes[0].url, {
             timeout: 5000,
             showProgressBar: true,
             closeOnClick: false,
             pauseOnHover: true,
             buttons: [
               {text: 'Yes', action: () => location.replace(redirectUri)},
               {text: 'No', action: (toast) => {
                   this.$snotify.remove(toast.id);
                   toastClosed = true;
                 }}
             ]
           }).on("destroyed", (toast => {
             if (!toastClosed) {
               location.replace(redirectUri)
             }
           }));
        }
      },
      isInSameScope(user) {
        const scopesUserConnected = this.$store.getters["auth/allScopes"];
        let isFound = false;
        scopesUserConnected.forEach(scope => {
          if (user.scopes.some(element => element.id === scope.id)) {
            isFound = true;
            return true;
          }
        });

        return isFound;
      },
      getRoles() {
        getAllRoles().then(listOfAllRoles => {
          this.roles = listOfAllRoles.data.data;
          this.roles.push({id: "Multirole",name: "Multirole"})
          this.updateRoleField(this.roles);
        })
      },
      updateRoleField(roles) {
        this.fields[3].filterable = roles.map(role => {
          return {value: role.name, text: role.name};
        });
      },
      updateScopeField(scopes) {
        this.fields[4].filterable = scopes.map(scope => {
          return {value: scope.name, text: scope.name};
        });
      },

       updateStatusField() {
        this.fields[5].filterable = [
          {
            value: 'completed', text: this.$t(this.$getConst.pages.pageUsers.signup_completed)
          },
          {
            value: 'expired', text: this.$t(this.$getConst.pages.pageUsers.invitation_expired)
          },
          {
            value: 'waiting', text: this.$t(this.$getConst.pages.pageUsers.invitation_sent)
          },
        ];
      },

      exportExcel() {
        let params ='';
        for (let item in this.filters) {
          if (this.filters[item]) {
              params += (!params?'?': '&' ) + item + '=' + this.filters[item]
          }
        }
        exportUsers(params,{
            responseType: 'blob',
            headers: {
              'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const today = new Date();
            const date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
            const time = today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
            const dateTime = date + ' ' + time;
            link.setAttribute('download', 'users' + '-' + dateTime + '.xlsx')
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
          },
          (error) => {
            this.handleApiError(error);
          })
      },

      usersHasSameScopeAsOrigin(user) {
        return user.scopes.find(scope => scope.url === window.location.origin);
      },

      goToUserDetails(userId) {
        this.$router.push({name: this.$getConst.routesNames.administration.userDetails, params: {user_id: userId}});
      },

      confirmDeleteUser(user) {
        this.userToDelete = user;
        this.$bvModal.show('confirmation-modal')
      },
      cancelDeleteUser() {
        this.userToDelete = null;
      },
      deleteUser() {
        deleteUser(this.userToDelete.id).then(
          () => {
            this.$refs.usersTable.removeItem(this.userToDelete);
            this.$refs.confirmDelete.close();
            this.$snotify.success(this.$t(this.$getConst.pages.pageUsers.user_deleted));
          },
          (error) => {
            this.handleApiError(error);
            this.$refs.confirmDelete.close();
          }
        )
      },

      enableOrDisableUser(userId) {
        this.enablingUserId = userId;
        enableOrDisableUser(userId).then(
          response => {
            this.$snotify.success(this.$t(this.$getConst.pages.pageUsers.user_updated));
            this.$refs.usersTable.editItem(response.data);
            this.enablingUserId = '';
          },
          error => {
            this.enablingUserId = '';
            if (error.response.data.code === 404) {
              this.$snotify.error(this.$t(this.$getConst.pages.alerts.user_not_found_in_azure_ad_b2c));
            } else {
              this.handleApiError(error);
            }
          }
        );
      }

    },

    beforeDestroy() {
      this.$root.$off('onAddUser');
      this.$root.$off('onEditUser');
    }
  }
</script>
